import { Topic } from "../entities/Topic";
import { htmlToRawText } from "../../../utils/stringUtils";
import { t } from "i18next";
import { copyTopicPartContentUseCase } from "../../topicPart/useCases/copyTopicPartContentUseCase";

const field = (
    key: string,
    value: string | null | undefined,
    multiline = false,
) => {
    if (!value?.trim()) return null;
    return t(key) + (multiline ? " :\n" : " : ") + value;
};

export async function buildTopicMarkdown(topic: Topic): Promise<string> {
    if (!topic) return "";

    const parts: string[] = [];

    if (topic.title) {
        parts.push(topic.title);
    }

    const description = field("topic.field.description", topic.description);
    if (description) parts.push(description);

    const introductionText = htmlToRawText(topic.introduction);
    const formattedIntroduction = field(
        "topic.field.introduction",
        introductionText,
        true,
    );
    if (formattedIntroduction) parts.push(formattedIntroduction);

    const synthesisText = htmlToRawText(topic.synthesis);
    const formattedSynthesis = field(
        "topic.field.synthesis",
        synthesisText,
        true,
    );
    if (formattedSynthesis) parts.push(formattedSynthesis);

    const conclusionText = htmlToRawText(topic.conclusion);
    const formattedConclusion = field(
        "topic.field.conclusion",
        conclusionText,
        true,
    );
    if (formattedConclusion) parts.push(formattedConclusion);

    if (topic.goals && topic.goals.length > 0) {
        const goalsText = topic.goals.filter((g) => g.trim()).join("\n- ");
        const formattedGoals = field(
            "topic.field.goals",
            goalsText ? "- " + goalsText : null,
            true,
        );
        if (formattedGoals) parts.push(formattedGoals);
    }

    if (topic.tags && topic.tags.length > 0) {
        const tagsText = topic.tags.filter((t) => t.trim()).join(", ");
        const formattedTags = field("topic.field.tags", tagsText);
        if (formattedTags) parts.push(formattedTags);
    }

    for (let index = 0; index < (topic.topicParts ?? []).length; index++) {
        const part = topic.topicParts?.[index];
        if (!part) continue;

        const text = await copyTopicPartContentUseCase.exec(part, true);

        if (text) {
            parts.push("---\n\nPart " + (index + 1) + " : " + text);
        }
    }

    return parts.join("\n\n");
}

export const copyTopicContentUseCase = {
    async exec(topic: Topic): Promise<void> {
        const copyValue = await buildTopicMarkdown(topic);
        if (!copyValue) return;

        try {
            await navigator.clipboard.writeText(copyValue);
        } catch (error) {
            console.error("Failed to copy topic content:", error);
            throw error;
        }
    },
};
